import React, { useCallback } from 'react'
import PropTypes from 'prop-types'
import { HeaderOne, HeaderThree, TextLink } from 'secondstep-components'
import NavigationHelper from 'layers/navigation/navigationHelper'
import ClassCard from 'components/dashboard/ClassCard'
import {
  getClassCardDisplayData,
  getClassCompleteLessons,
  getClassInstanceId,
  getClassTier,
  getClassTitle,
  getClassTotalLessons,
} from './classListHelpers'
import TierLabel from 'components/TierLabel'
import {
  ButtonsContainer,
  StyledAwardIcon,
  StyledButton,
  StyledCreateClassIcon,
  Wrapper,
} from './component.styles'
import { PreviewCourseList } from './PreviewCourseList'
import {
  getDigitalWellBeingPreviewPath,
  getTier2DisplayTitle,
  getTier2PreviewPath,
  isDigitalWellBeingCapabilityEnabledForProgram,
} from './helpers'
import {
  DIGITAL_WELL_BEING_CLASSES_TITLE,
  DIGITAL_WELL_BEING_TEACH_DESCRIPTION,
  EXPLORE_PROGRAM_DESCRIPTION,
  EXPLORE_PROGRAM_TITLE,
  MY_CLASSES_TITLE,
  TEACH_DESCRIPTION,
  NO_CLASSES_MESSAGE,
  NO_CLASSES_TRAINING_LINK,
  TIER_1_LESSONS_LABEL,
} from './constants'

// #region  Section Header
const SectionHeader = ({ title, tierLabel, description, dataTestId }) => (
  <header>
    <div className="header-text">
      <div className="headline">
        <HeaderThree dataTestId={dataTestId}>{title}</HeaderThree>
        {tierLabel && <TierLabel labelText={tierLabel} />}
        <div className="spacer" />
      </div>
      <p>{description}</p>
    </div>
  </header>
)

SectionHeader.propTypes = {
  dataTestId: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  tierLabel: PropTypes.string,
  title: PropTypes.string.isRequired,
}
// #endregion

const createClassCardClickHandler = ({
  tier,
  gradeSlug,
  classItem,
  onComponentClick,
  onClassClick,
  program,
}) => path => {
  // DWB needs onClassClick so site query params are preserved in teach routes.
  if (onClassClick && program === 'digital-well-being' && tier === 1) {
    return onClassClick(gradeSlug, getClassInstanceId(classItem))()
  }
  // K8 class cards build the teach URL in ClassCard (same as dashboard tiles).
  if (onComponentClick) {
    return onComponentClick(path)
  }
  if (onClassClick && tier === 1) {
    return onClassClick(gradeSlug, getClassInstanceId(classItem))()
  }
  return undefined
}

const getPreviewClickHandler = ({
  onComponentClick,
  onCourseClick,
  program,
  getPath,
}) => course =>
  onComponentClick
    ? () => onComponentClick(getPath({ program, course }))
    : onCourseClick(course.slug)

// #region  Class List
const ClassList = ({
  classes,
  courseLinks,
  courseManager,
  lessonCompletionGoalValue,
  onComponentClick,
  onClassClick,
  program,
  tier = 1,
}) => (
  <ul className="class-links">
    {classes.map(classItem => {
      const classInstanceId = getClassInstanceId(classItem)
      const classTitle = getClassTitle(classItem)
      const {
        defaultUnit,
        gradeDisplayTitle,
        gradeSlug,
      } = getClassCardDisplayData({ classItem, courseLinks, tier, program })

      return (
        <li key={`class-card-li-${classInstanceId}`}>
          <ClassCard
            className={classTitle}
            dataTestId={`class-card-${classInstanceId}`}
            defaultUnit={tier === 2 ? defaultUnit : undefined}
            displayTitle={classTitle}
            goalPercent={tier === 1 ? lessonCompletionGoalValue : undefined}
            gradeDisplayTitle={gradeDisplayTitle}
            includeProgressBar={tier === 1}
            instance={classInstanceId}
            onClick={createClassCardClickHandler({
              tier,
              gradeSlug,
              classItem,
              onComponentClick,
              onClassClick,
              program,
            })}
            program={program}
            scoID={classItem?.scoId ?? classItem?.scoID}
            tier={tier}
            totalAmount={getClassTotalLessons(
              classItem,
              courseManager,
              program,
            )}
            totalComplete={getClassCompleteLessons(classItem)}
          />
        </li>
      )
    })}
  </ul>
)

ClassList.propTypes = {
  classes: PropTypes.arrayOf(
    PropTypes.shape({
      class_title: PropTypes.string,
      completeLessons: PropTypes.number,
      contentfulID: PropTypes.string,
      course_name: PropTypes.string,
      instance: PropTypes.string,
      registration_id: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]),
      scoId: PropTypes.number,
      title: PropTypes.string,
      totalLessons: PropTypes.number,
    }),
  ),
  courseLinks: PropTypes.arrayOf(
    PropTypes.shape({
      entryId: PropTypes.string,
      slug: PropTypes.string,
      title: PropTypes.string,
    }),
  ),
  courseManager: PropTypes.object,
  lessonCompletionGoalValue: PropTypes.number,
  onClassClick: PropTypes.func,
  onComponentClick: PropTypes.func,
  program: PropTypes.string,
  tier: PropTypes.number,
}
// #endregion

export const ProductLessons = React.forwardRef(
  (
    {
      classes,
      courseLinks,
      courseManager,
      currentSite,
      lessonCompletionGoalValue,
      onClassClick,
      onComponentClick,
      onCourseClick,
      onCreateClass,
      onCreateSmallGroup,
      program,
      showSmallGroupCreation = true,
      title,
    },
    ref,
  ) => {
    const userHasClasses = !!classes && classes.length > 0
    const isDigitalWellBeingProduct = program === 'digital-well-being'
    const classesSectionTitle = isDigitalWellBeingProduct
      ? DIGITAL_WELL_BEING_CLASSES_TITLE
      : MY_CLASSES_TITLE
    const classesSectionDescription = isDigitalWellBeingProduct
      ? DIGITAL_WELL_BEING_TEACH_DESCRIPTION
      : TEACH_DESCRIPTION

    const tier1Classes = classes?.filter(c => getClassTier(c) !== 2) || []
    const tier2Classes = classes?.filter(c => getClassTier(c) === 2) || []
    const hasTier2Classes = tier2Classes.length > 0

    // TODO: replace the shouldShowOnLessonsPage filter with the real DWB
    // course filter once the DWB content is wired up in contentful.
    const lessonPageCourseLinks =
      courseLinks?.filter(c => c.shouldShowOnLessonsPage) || []
    const tier2CourseLinks =
      courseLinks?.filter(c => !c.shouldShowOnLessonsPage) || []

    const showDigitalWellBeingUnit =
      isDigitalWellBeingProduct &&
      isDigitalWellBeingCapabilityEnabledForProgram({
        site: currentSite,
        program,
      })

    const handleTier1CoursePreviewClick = course => onCourseClick(course.slug)

    const handleTier2CoursePreviewClick = getPreviewClickHandler({
      onComponentClick,
      onCourseClick,
      program,
      getPath: getTier2PreviewPath,
    })

    const handleDigitalWellBeingCoursePreviewClick = getPreviewClickHandler({
      onComponentClick,
      onCourseClick,
      program,
      getPath: getDigitalWellBeingPreviewPath,
    })

    const navigateTo = path =>
      onComponentClick?.(path, NavigationHelper.types.PUSH)

    const handleCreateClass = useCallback(() => {
      if (onCreateClass) onCreateClass()
    }, [onCreateClass])

    const handleCreateSmallGroup = useCallback(() => {
      if (onCreateSmallGroup) {
        onCreateSmallGroup()
        return
      }
      if (onCreateClass) onCreateClass()
    }, [onCreateSmallGroup, onCreateClass])

    return (
      <Wrapper>
        <header>
          <HeaderOne>{title}</HeaderOne>
        </header>

        <section>
          <SectionHeader
            dataTestId={
              isDigitalWellBeingProduct
                ? 'header-digital-well-being-classes'
                : 'header-my-classes'
            }
            description={classesSectionDescription}
            tierLabel={isDigitalWellBeingProduct ? undefined : 'TIER 1'}
            title={classesSectionTitle}
          />
          <div>
            {!userHasClasses && !isDigitalWellBeingProduct && (
              <div className="no-classes">
                <p className="no-class-description">{NO_CLASSES_MESSAGE}</p>
                <p>
                  {NO_CLASSES_TRAINING_LINK}{' '}
                  <TextLink href="/dashboard">My Programs</TextLink>.
                </p>
              </div>
            )}
            {userHasClasses && (
              <ClassList
                classes={tier1Classes}
                courseLinks={courseLinks}
                courseManager={courseManager}
                lessonCompletionGoalValue={lessonCompletionGoalValue}
                onClassClick={onClassClick}
                onComponentClick={onComponentClick}
                program={program}
                tier={1}
              />
            )}
          </div>
          <ButtonsContainer>
            <StyledButton onClick={handleCreateClass}>
              <StyledCreateClassIcon />
              {'Create a Class'}
            </StyledButton>
            {!isDigitalWellBeingProduct && (
              <StyledButton
                onClick={() => navigateTo(`/product/${program}/trainings`)}
              >
                <StyledAwardIcon />
                {'Training Articles'}
              </StyledButton>
            )}
          </ButtonsContainer>
        </section>

        {showSmallGroupCreation && (
          <section id="small-groups">
            <SectionHeader
              dataTestId="header-small-groups"
              description="Teach Tier 2 lessons to small groups and track progress"
              tierLabel="TIER 2"
              title="Small Groups"
            />
            <div>
              {hasTier2Classes && (
                <ClassList
                  classes={tier2Classes}
                  courseLinks={courseLinks}
                  courseManager={courseManager}
                  lessonCompletionGoalValue={lessonCompletionGoalValue}
                  onClassClick={onClassClick}
                  onComponentClick={onComponentClick}
                  program={program}
                  tier={2}
                />
              )}
            </div>
            <ButtonsContainer>
              <StyledButton onClick={handleCreateSmallGroup}>
                <StyledCreateClassIcon aria-hidden />
                {'Create a Small Group'}
              </StyledButton>
              <StyledButton
                onClick={() =>
                  navigateTo(`/product/${program}/intervention-resources`)
                }
              >
                <StyledAwardIcon />
                {'Intervention Resources'}
              </StyledButton>
            </ButtonsContainer>
          </section>
        )}

        <section id="explore-the-program">
          <SectionHeader
            dataTestId="header-explore-the-program"
            description={EXPLORE_PROGRAM_DESCRIPTION}
            title={EXPLORE_PROGRAM_TITLE}
          />

          {!showDigitalWellBeingUnit && (
            <>
              <HeaderThree dataTestId="header-tier-1-lessons">
                {TIER_1_LESSONS_LABEL}
              </HeaderThree>
              <PreviewCourseList
                courseLinks={lessonPageCourseLinks}
                getOnClick={handleTier1CoursePreviewClick}
                getTitle={course => course.title}
                tier={1}
              />
            </>
          )}

          {showDigitalWellBeingUnit && (
            <PreviewCourseList
              courseLinks={lessonPageCourseLinks}
              getOnClick={handleDigitalWellBeingCoursePreviewClick}
              getTitle={course => course.title}
              isDigitalWellBeing
              tier={1}
            />
          )}

          {!isDigitalWellBeingProduct && tier2CourseLinks.length > 0 && (
            <>
              <HeaderThree dataTestId="header-tier-2-lessons">
                Tier 2 Lessons
              </HeaderThree>
              <PreviewCourseList
                courseLinks={tier2CourseLinks}
                getOnClick={handleTier2CoursePreviewClick}
                getTitle={getTier2DisplayTitle}
                tier={2}
              />
            </>
          )}
        </section>
      </Wrapper>
    )
  },
)

ProductLessons.propTypes = {
  classes: PropTypes.arrayOf(
    PropTypes.shape({
      class_title: PropTypes.string,
      completeLessons: PropTypes.number,
      contentfulID: PropTypes.string,
      course_name: PropTypes.string,
      instance: PropTypes.string,
      registration_id: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]),
      scoId: PropTypes.number,
      title: PropTypes.string,
      totalLessons: PropTypes.number,
    }),
  ),
  courseLinks: PropTypes.arrayOf(
    PropTypes.shape({
      slug: PropTypes.string,
      title: PropTypes.string,
    }),
  ),
  courseManager: PropTypes.object,
  currentSite: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    preferences: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        value: PropTypes.any,
      }),
    ),
  }),
  lessonCompletionGoalValue: PropTypes.number,
  onClassClick: PropTypes.func,
  onComponentClick: PropTypes.func,
  onCourseClick: PropTypes.func.isRequired,
  onCreateClass: PropTypes.func,
  onCreateSmallGroup: PropTypes.func,
  program: PropTypes.string,
  showSmallGroupCreation: PropTypes.bool,
  title: PropTypes.string.isRequired,
}
export default ProductLessons
